import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogOut, Globe, ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n, { LANGUAGES } from '../../i18n';
import { cn } from '../../lib/utils';
import { Avatar } from '../ui/Avatar';
import { useAuthStore } from '../../store/auth';
import { getNavSections } from '../../lib/roles';
import { useBranding } from '../../lib/branding';
import { BrandLogo } from '../BrandLogo';
import type { UserRole } from '../../types';
import { CommandPalette } from './CommandPalette';

const FOLDED_KEY = 'xcodec-nav-folded';

// Folded groups are a per-browser convenience; storage can be unavailable.
function loadFolded(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(FOLDED_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

function saveFolded(v: Record<string, boolean>) {
  try {
    localStorage.setItem(FOLDED_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

function isActivePath(to: string, path: string): boolean {
  return to === '/' ? path === '/' : path.startsWith(to);
}

export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const location = useLocation();
  // A restreamer's login reaches the portal only: no panel settings, no catalogue search.
  const isPortal = user?.role === 'restreamer';
  const { name: panelName } = useBranding();

  const navSections = user?.role ? getNavSections(user.role as UserRole, t) : [];
  const [folded, setFolded] = useState(loadFolded);
  const [searchOpen, setSearchOpen] = useState(false);
  const toggleGroup = (id: string, isFolded: boolean) => {
    const next = { ...folded, [id]: !isFolded };
    setFolded(next);
    saveFolded(next);
  };

  return (
    <aside
      id="app-sidebar"
      className={cn(
        'w-60 shrink-0 flex flex-col h-full bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700/60',
        // A drawer below lg, hidden from keyboard and screen readers while closed.
        'fixed inset-y-0 start-0 z-40 transition-[transform,visibility] duration-200 ease-out',
        'lg:static lg:z-auto lg:visible lg:ltr:translate-x-0 lg:rtl:translate-x-0',
        open ? 'visible translate-x-0' : 'invisible ltr:-translate-x-full rtl:translate-x-full'
      )}
    >
      {/* Logo */}
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-gray-200 dark:border-slate-700/60 shrink-0">
        <BrandLogo box="h-7 w-7" className="rounded-lg" />
        <span className="text-sm font-bold text-gray-900 dark:text-slate-100 tracking-tight truncate">{panelName}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('nav.close_menu', 'Close menu')}
          className="lg:hidden ms-auto h-8 w-8 -me-2 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Quick search */}
      {!isPortal && (
      <div className="px-2.5 pt-3 shrink-0">
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="w-full flex items-center gap-2 h-9 px-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 text-sm text-gray-500 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
        >
          <Search size={14} />
          <span className="flex-1 text-left">{t('nav.search', 'Search…')}</span>
          <kbd className="text-[10px] font-semibold rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5 py-px">Ctrl K</kbd>
        </button>
      </div>
      )}
      {!isPortal && <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} sections={navSections} />}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navSections.map((section) => {
          const key = section.id ?? section.title;
          const hasActive = section.items.some((i) => isActivePath(i.to, location.pathname));
          // A single-item group (Dashboard) has nothing to fold.
          const foldable = section.items.length > 1;
          const isFolded = foldable && !hasActive && (folded[key] ?? section.defaultFolded ?? false);
          return (
          <div key={key} className="mb-3">
            {foldable ? (
              <button
                type="button"
                onClick={() => !hasActive && toggleGroup(key, isFolded)}
                aria-expanded={!isFolded}
                className={cn(
                  'w-full flex items-center justify-between px-3 mb-1 h-6 rounded-md text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500',
                  hasActive ? 'cursor-default' : 'hover:text-gray-600 dark:hover:text-slate-300'
                )}
              >
                <span>{section.title}</span>
                <span className="flex items-center gap-1.5">
                  {isFolded && (
                    <span className="normal-case tracking-normal rounded-full bg-gray-100 dark:bg-slate-800 px-1.5 text-[10px]">{section.items.length}</span>
                  )}
                  <ChevronDown size={12} className={cn('transition-transform', isFolded && '-rotate-90')} />
                </span>
              </button>
            ) : (
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500">
                {section.title}
              </p>
            )}
            {!isFolded && section.items.map((item) => {
              const isActive = isActivePath(item.to, location.pathname);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors mb-0.5',
                    isActive
                      ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-100'
                  )}
                >
                  <span className={cn('shrink-0', isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-slate-500')}>
                    <item.Icon size={16} />
                  </span>
                  {item.label}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* Language switcher */}
      <div className="px-3 pb-2 shrink-0">
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
          <Globe size={12} className="text-gray-400 dark:text-slate-500 shrink-0" />
          <select
            value={i18n.language.split('-')[0]}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="flex-1 text-xs text-gray-600 dark:text-slate-400 bg-transparent border-none outline-none cursor-pointer"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* User info at bottom */}
      {user && (
        <div className="border-t border-gray-200 dark:border-slate-700/60 p-3 shrink-0">
          <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            <Avatar name={user.username} size="sm" online />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-slate-100 truncate">{user.username}</p>
              <p className="text-[11px] text-gray-400 dark:text-slate-500 capitalize">
                {t(`roles.${user.role}` as never) || user.role}
              </p>
            </div>
            <button
              onClick={logout}
              className="p-1 rounded-md text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={t('nav.sign_out')}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
